import _ from 'lodash';
import Category from 'models/Category';

/**
 * What the category cell offers while she types.
 *
 * Pulled out of the editor because the ranking is the part with an opinion in it, and an opinion
 * about ordering is invisible from the markup: a list that puts "Antoine/Jasmine" above "Antoine"
 * for the query "antoine" looks like a working autocomplete right up until she picks the wrong one
 * without reading it.
 */

/**
 * The entry that means "filed under nothing".
 *
 * Every other category select in the app carries one -- `CategorySelect` puts its "Unknown" row at
 * the top of the menu -- and it is the same word here so that the thing she picks in the ledger and
 * the thing she picks on the transactions tab read as the same thing. Its id is `null`, which is
 * what the cell commits to clear itself, so picking it needs no special case downstream.
 *
 * It carries no group, because there is no group it belongs to, and the blank flag is what lets the
 * list draw it as the odd one out rather than as a category called Unknown.
 */
export const noCategoryChoice = {
	id: null,
	name: 'Unknown',
	fullName: 'Unknown',
	parentName: null,
	groupName: '',
	typeName: null,
	deleted: false,
	blank: true,
};

/**
 * Everything the category cell offers: the blank entry first, then the categories themselves.
 *
 * The blank leads because it is the shortest way out of a cell filed by mistake -- one keystroke
 * from opening -- and because the highlight starts on whatever the cell already holds, which for an
 * uncategorised row is this. Kept apart from `availableCategoriesForMonths()` so that the list of
 * actual categories stays a list of actual categories for anything that wants one.
 */
export function categoryChoicesForMonths(categoryGroupsByType, selectedDates) {
	return [noCategoryChoice].concat(availableCategoriesForMonths(categoryGroupsByType, selectedDates));
}

/**
 * Every category the picker may offer for the months on screen, flattened.
 *
 * `CategoryGroup::getVisibleCategoryForMonths()` decides which those are, rather than a filter of
 * our own. Left to its default it keeps every live category and drops a deleted one *unless* it
 * has activity in these months -- a budget or a transaction -- which is a better version of the
 * rule this used to hand-roll: a row filed under a category deleted since is activity, so the
 * category it points at stays offerable without anyone having to pass its id in as a special case.
 * That is what the months are for here, and why they belong in the name.
 *
 * The second argument is deliberately left at its default. Passing `true` additionally drops a
 * category with no activity when `show_when_not_used` is off, which is the rule the budget block
 * wants -- it is about how dense the block is. A picker is not a block: 10 of this budget's 86 live
 * categories have that flag off, and dropping them would make a category unfilable in exactly the
 * months it has nothing in it yet, when filing a row is the only way to give it something.
 *
 * The group and the parent travel with each choice because names repeat and neither alone is
 * enough: her sheet has a Jasmine under Date Nights and a Jasmine under Income, and this budget has
 * a Beauty under Antoine Extra and a Beauty under Jasmine Extra -- same group, same name, two
 * different people's money.
 *
 * @param categoryGroupsByType {Object} from `getAllCategoryGroupsHashedByType()`
 * @param selectedDates {Object} the months on screen, keyed by year-month
 */
export function availableCategoriesForMonths(categoryGroupsByType, selectedDates) {
	const choices = [];
	_.each(categoryGroupsByType, (categoryGroups, type) => {
		_.each(categoryGroups, (categoryGroup) => {
			categoryGroup.getVisibleCategoryForMonths(selectedDates).forEach((category) => {
				choices.push({
					id: category.id,
					name: category.name,
					// "Antoine Extra -> Beauty". Falls back to the bare name on a group whose
					// parents have not been linked up yet, which is what `fullName()` does.
					fullName: category.fullName(),
					// The parent on its own, because the list draws the two halves differently:
					// the name is what she is spelling and the parent is what tells two of them
					// apart, and run together in one string the half she is reading is buried in
					// the middle of it. Null for a category that has no parent.
					parentName: category.getParent() ? category.getParent().name : null,
					groupName: categoryGroup.name,
					typeName: Category.typesNames[type],
					// Only ever a category with money in it this month; see above.
					deleted: !!category.deleted,
				});
			});
		});
	});
	return choices;
}

/**
 * The choices worth showing for what has been typed so far, best first.
 *
 * Anything starting with the query comes before anything merely containing it, and within each of
 * those the original order is kept -- which is the order the groups are in on the budget block, so
 * the list reads the way the screen beside it does. An empty query is not a search and returns
 * everything.
 *
 * Matching is on the category name alone. Group names are shown but not searched: typing "static"
 * to mean "everything under Static Expenditures" is a filter, not a completion, and it would put a
 * dozen unrelated names above the one she was halfway through spelling.
 *
 * @param choices {Array} from `availableCategoriesForMonths()`
 * @param query {string} what is in the cell
 */
export default function matchCategories(choices, query) {
	const needle = (query || '').trim().toLowerCase();
	if(!needle) {
		return choices;
	}
	const starting = [];
	const containing = [];
	choices.forEach((choice) => {
		const name = (choice.name || '').toLowerCase();
		if(name.startsWith(needle)) {
			starting.push(choice);
		} else if(name.includes(needle)) {
			containing.push(choice);
		}
	});
	return starting.concat(containing);
}

/** The choice a typed name lands on exactly, for committing without ever opening the list. */
export function exactMatch(choices, query) {
	const needle = (query || '').trim().toLowerCase();
	if(!needle) {
		return null;
	}
	return choices.find((choice) => (choice.name || '').toLowerCase() === needle) || null;
}
